import { useEffect, useMemo, useRef, useState } from 'react';
import { FormErrorBanner } from '../FormErrorBanner';
import { InlineLoader } from '../loading';
import {
  hiringLinksAPI,
  type HiringRaiseTeam,
  type HiringRaiseTeamOptions,
  type HiringStaff,
} from '../../services/api';
import { FormRow, FormSubheading } from './HiringForm';
import { HiringChipsSelect, HiringSelect, type HiringSelectOption } from './HiringSelect';
import {
  NOBODY_NOT_ALLOWED,
  RAISE_FORM,
  RAISE_WORDS,
  TEAM_WORDS,
  isTeamReadFailure,
  raiseErrorText,
  teamEmptyRoleOf,
  teamDraftOf,
  teamOfDraft,
  teamProblem,
  type TeamDraft,
  type TeamDraftRound,
  type TeamNotAllowed,
  type TeamRoleList,
} from './hiringText';

/**
 * The Recruitment team: who Recruitment puts on the opening. The person picks the
 * recruiters (also sent as the schedulers, as 1Office does), the offer makers, and one
 * interviewer per interview round, from the people whose grade is set for that round; for
 * a round the client takes, the client's name, company e-mail and (optionally) mobile. It
 * opens filled with the budget's saved defaults, and every part can be changed. Changing
 * the budget reads it again and fills it again.
 *
 * Used by the Raise hiring window (raise mode only: a person who asks picks no team) and by
 * the hiring request window of the person who confirms. Since 15 Oct 2026 it is drawn as
 * rows of the form: recruiters and offer makers as chips with "Add…", one searchable
 * select per round, a client round's three fields on one row. Staff are named by name with
 * their code beside it, read fresh from Recruitment; the names are shown and never kept.
 *
 * The chips fields open from a click anywhere in them (15 Oct 2026; until then only the small
 * "Add…" words did) and list everyone 1Office lists. The server never sends an empty role
 * list or a staff round with nobody (an empty role list is a 503, a round nobody can take a
 * 422), so a drawn list always holds someone. A team that could not be read (no answer, or
 * a 5xx) still draws the Recruiters and Offer makers rows under the server's own sentence,
 * each saying so in one line in its open list, with "Try again". One error comes for the
 * whole team, so a row names its own list only where the server's 503 does (`emptyRole`):
 * "1Office listed no recruiters just now."; any other row says "Could not read the team
 * from 1Office."
 */

export interface HiringTeamState {
  /** The lists and rounds as Recruitment answered; null while they are read, or when they could not be. */
  options: HiringRaiseTeamOptions | null;
  /** The server's sentence when the team could not be read. */
  error: string | null;
  /** The error is a failed READ (no answer, Recruitment failing), not a refusal about the budget. */
  readFailed: boolean;
  /** The role list 1Office answered empty, when the server's refusal names one. */
  emptyRole: TeamRoleList | null;
  /** The team as the person has it now; null until the lists are read. */
  draft: TeamDraft | null;
  change: (next: (draft: TeamDraft) => TeamDraft) => void;
  /** Read the lists again, and fill the team again from the defaults. */
  reload: () => void;
  /** What the team still needs, in one sentence; null when it is complete (or not read yet). */
  problem: string | null;
  /** Read and complete: Review may be pressed. */
  ready: boolean;
  /** The team as it is sent; null until it is read. */
  team: HiringRaiseTeam | null;
  /** People a refusal said no longer hold their role: their cards are marked. */
  notAllowed: TeamNotAllowed;
  markNotAllowed: (people: TeamNotAllowed) => void;
}

/**
 * Reads the team section's lists for a budget, and keeps the team the person picks. Reads
 * nothing while `enabled` is false or no budget is chosen. `onRefused` is told of a refusal
 * first: it returns true when it acted on it (hiring or raising switched off).
 */
export function useHiringTeam({ projectId, budgetId, enabled, onRefused }: {
  projectId: number;
  budgetId: number | null;
  enabled: boolean;
  onRefused?: (err: unknown) => boolean;
}): HiringTeamState {
  const [reads, setReads] = useState(0);
  const [options, setOptions] = useState<HiringRaiseTeamOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [emptyRole, setEmptyRole] = useState<TeamRoleList | null>(null);
  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [notAllowed, setNotAllowed] = useState<TeamNotAllowed>(NOBODY_NOT_ALLOWED);
  const refused = useRef(onRefused);
  refused.current = onRefused;

  useEffect(() => {
    setOptions(null);
    setError(null);
    setReadFailed(false);
    setEmptyRole(null);
    setDraft(null);
    setNotAllowed(NOBODY_NOT_ALLOWED);
    if (!enabled || budgetId == null) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => hiringLinksAPI.raiseTeam(projectId, budgetId))
      .then((res) => {
        if (cancelled) return;
        const answer = res?.data?.data;
        if (!answer || !Array.isArray(answer.recruiters) || !Array.isArray(answer.offerMakers) || !Array.isArray(answer.rounds)) {
          setError(TEAM_WORDS.unreadable);
          setReadFailed(true);
          return;
        }
        setOptions(answer);
        setDraft(teamDraftOf(answer));
      })
      .catch((err: unknown) => {
        if (cancelled || refused.current?.(err)) return;
        setError(raiseErrorText(err, TEAM_WORDS.failed));
        setReadFailed(isTeamReadFailure(err));
        setEmptyRole(teamEmptyRoleOf(err));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, budgetId, enabled, reads]);

  const problem = teamProblem(draft);
  const team = useMemo(() => (draft ? teamOfDraft(draft) : null), [draft]);
  return {
    options,
    error,
    readFailed,
    emptyRole,
    draft,
    change: (next) => setDraft((was) => (was ? next(was) : was)),
    reload: () => setReads((n) => n + 1),
    problem,
    ready: !!options && !!draft && problem === null,
    team,
    notAllowed,
    markNotAllowed: setNotAllowed,
  };
}

const FIELD = 'h-9 w-full min-w-0 rounded-lg border border-gray-300 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100';

/** A member of staff as an option: the name, the code after it. */
const staffOption = (person: HiringStaff): HiringSelectOption => ({
  value: String(person.userId),
  label: String(person.name ?? '').trim() || String(person.empCode ?? person.userId),
  meta: person.name && person.empCode ? person.empCode : null,
});

/** What a row says when the team could not be read: in its open list (with "Try again"), and in the closed field. */
interface RowFailure { line: string; short: string }

/**
 * Recruiters, or offer makers: the people picked as chips, and "Add…". At least one. `failed`:
 * the team could not be read - the field says so, and its open list offers "Try again".
 */
function PeopleRow({ title, info, what, list, picked, marked, limit, onChange, busy, failed = null, onRetry }: {
  title: string;
  /** The row's (i): none on the approved form (15 Oct 2026, panel 5). */
  info?: string;
  what: 'recruiter' | 'offer maker';
  list: readonly HiringStaff[];
  picked: readonly number[];
  marked: readonly number[];
  limit: number;
  onChange: (ids: number[]) => void;
  busy: boolean;
  failed?: RowFailure | null;
  onRetry?: () => void;
}) {
  const options = useMemo(() => {
    const known = list.map(staffOption);
    // Someone picked who is no longer listed keeps a chip (by code), so it can be removed.
    const missing = picked.filter((id) => !list.some((p) => p.userId === id))
      .map((id) => ({ value: String(id), label: String(id) }));
    return [...known, ...missing];
  }, [list, picked]);
  return (
    <FormRow label={title} info={info}>
      <HiringChipsSelect
        label={title}
        addLabel={TEAM_WORDS.addLabel(what)}
        values={picked.map(String)}
        options={options}
        onChange={(values) => onChange(values.map(Number))}
        removeLabel={(option) => TEAM_WORDS.remove(option.meta ? `${option.label} (${option.meta})` : option.label)}
        marked={marked.map(String)}
        markedHover={TEAM_WORDS.notAllowed}
        limit={limit}
        searchPlaceholder={TEAM_WORDS.searchPlaceholder}
        placeholder={failed ? failed.short : TEAM_WORDS.choose(what)}
        emptyText={TEAM_WORDS.noMatch}
        allPickedText={TEAM_WORDS.allPicked}
        error={failed ? failed.line : null}
        onRetry={failed ? onRetry : undefined}
        retryText={RAISE_WORDS.tryAgain}
        disabled={busy}
      />
    </FormRow>
  );
}

/** One interview round taken by the client: name, e-mail and (optionally) mobile, on one row. */
function ClientRound({ n, round, onChange, limit, busy }: {
  n: number;
  round: Extract<TeamDraftRound, { kind: 'client' }>;
  onChange: (round: Extract<TeamDraftRound, { kind: 'client' }>) => void;
  limit: number;
  busy: boolean;
}) {
  return (
    <FormRow label={TEAM_WORDS.roundClient(n)}>
      {/* The mobile box is wide enough for its whole "Mobile (optional)". */}
      <div className="grid gap-2 sm:grid-cols-[1fr_1.2fr_minmax(9.5rem,0.8fr)]">
        <input
          type="text" aria-label={TEAM_WORDS.clientFieldLabel(n, 'name')} placeholder={TEAM_WORDS.clientName} value={round.name}
          maxLength={limit} disabled={busy} onChange={(e) => onChange({ ...round, name: e.target.value })} className={FIELD}
        />
        <input
          type="email" aria-label={TEAM_WORDS.clientFieldLabel(n, 'email')} placeholder={TEAM_WORDS.clientEmail} value={round.email}
          maxLength={limit} disabled={busy} onChange={(e) => onChange({ ...round, email: e.target.value })} className={FIELD}
        />
        <input
          type="text" inputMode="numeric" aria-label={TEAM_WORDS.clientFieldLabel(n, 'mobile')} placeholder={TEAM_WORDS.clientMobile}
          value={round.mobile} maxLength={10} disabled={busy}
          onChange={(e) => onChange({ ...round, mobile: e.target.value.replace(/\D/g, '') })} className={FIELD}
        />
      </div>
    </FormRow>
  );
}

/**
 * The section itself, as rows of the form: Recruiters and Offer makers as chips, one select
 * per round (a client round's name, e-mail and mobile on one row). `team` is the state
 * useHiringTeam keeps.
 */
export function HiringTeamSection({ team, busy }: { team: HiringTeamState; busy: boolean }) {
  const { options, draft, error } = team;
  const limit = options?.limits?.people ?? 20;
  const clientLimit = options?.limits?.clientText ?? 100;
  const setRound = (i: number, round: TeamDraftRound) =>
    team.change((was) => ({ ...was, rounds: was.rounds.map((r, j) => (j === i ? round : r)) }));

  return (
    <section aria-label={TEAM_WORDS.heading}>
      <FormSubheading note={options && !options.defaultsSaved ? TEAM_WORDS.noDefaults : null}>{RAISE_FORM.team}</FormSubheading>
      {error ? (
        <>
          <div className="space-y-2 py-2">
            <FormErrorBanner message={error} />
            <button type="button" className="text-sm text-primary-600 hover:underline" onClick={team.reload}>{RAISE_WORDS.tryAgain}</button>
          </div>
          {/* A failed read: the two fields are still drawn, and say so when opened - never a silent empty box. */}
          {team.readFailed && ([['recruiters', 'recruiter'], ['offerMakers', 'offer maker']] as const).map(([role, what]) => (
            <PeopleRow
              key={role}
              title={role === 'recruiters' ? TEAM_WORDS.recruiters : TEAM_WORDS.offerMakers}
              what={what}
              list={[]}
              picked={[]}
              marked={[]}
              limit={limit}
              onChange={() => undefined}
              busy={busy}
              failed={team.emptyRole === role
                ? { line: TEAM_WORDS.listedNone(what), short: TEAM_WORDS.listedNoneShort(what) }
                : { line: TEAM_WORDS.teamUnread, short: TEAM_WORDS.teamUnreadShort }}
              onRetry={team.reload}
            />
          ))}
        </>
      ) : !options || !draft ? (
        <InlineLoader label={TEAM_WORDS.loading} className="flex py-2" />
      ) : (
        <>
          <PeopleRow
            title={TEAM_WORDS.recruiters}
            what="recruiter"
            list={options.recruiters}
            picked={draft.recruiters}
            marked={team.notAllowed.recruiters}
            limit={limit}
            onChange={(ids) => team.change((was) => ({ ...was, recruiters: ids }))}
            busy={busy}
          />
          <PeopleRow
            title={TEAM_WORDS.offerMakers}
            what="offer maker"
            list={options.offerMakers}
            picked={draft.offerMakers}
            marked={team.notAllowed.offerMakers}
            limit={limit}
            onChange={(ids) => team.change((was) => ({ ...was, offerMakers: ids }))}
            busy={busy}
          />
          {options.rounds.map((round, i) => {
            const n = i + 1;
            const held = draft.rounds[i];
            if (round.kind === 'client' || held?.kind === 'client') {
              const typed = held?.kind === 'client' ? held : { kind: 'client' as const, name: '', email: '', mobile: '' };
              return <ClientRound key={n} n={n} round={typed} limit={clientLimit} onChange={(next) => setRound(i, next)} busy={busy} />;
            }
            const userId = held?.kind === 'staff' ? held.userId : null;
            const isMarked = userId != null && team.notAllowed.interviewers.some((one) => one.round === n && one.userId === userId);
            return (
              <FormRow key={n} label={TEAM_WORDS.round(n)}>
                <HiringSelect
                  label={TEAM_WORDS.round(n)}
                  value={userId != null ? String(userId) : ''}
                  onChange={(value) => setRound(i, { kind: 'staff', userId: value ? Number(value) : null })}
                  options={round.interviewers.map(staffOption)}
                  placeholder={TEAM_WORDS.nobodyPicked}
                  searchPlaceholder={TEAM_WORDS.searchPlaceholder}
                  emptyText={TEAM_WORDS.noMatch}
                  invalid={isMarked}
                  title={isMarked ? TEAM_WORDS.notAllowed : undefined}
                  disabled={busy}
                />
              </FormRow>
            );
          })}
        </>
      )}
    </section>
  );
}

export default HiringTeamSection;
